// Sugalarm — voice quiet hours (cycle 330).
//
// SINGLE SOURCE OF TRUTH for the overnight-wrap rule. Both consumers —
// the auto-announce scheduler (`hooks/useAutoAnnounce.ts`) and the spoken
// -reminder effect in `App.tsx` — import `isWithinQuietHours` from here.
// Do NOT re-derive the wrap inline at either call site; a second copy is
// how the two surfaces drift apart.
//
// SCOPE — read this before touching anything here:
//   Quiet hours pause the VOICE. That is all. They gate exactly two
//   things: the auto-announce cadence, and the spoken half of a fired
//   reminder (the reminder's toast + push still arrive).
//
//   They must NEVER suppress an alert, a push, an alarm sound, the urgent
//   overlay, or the manual Speak Status / Speak sample buttons — those
//   work at any hour, quiet hours or not. This is a different, narrower
//   feature than the alert/notification quiet hours that were permanently
//   removed in cycle 216 at the operator's explicit request, and it must
//   not grow back into it.
//
// Times are LOCAL wall-clock "HH:MM" strings (never UTC) — the same
// convention as `alarm_clock.start`/`end`, and the wrap is interpreted
// identically: `end <= start` means the window crosses midnight.
// Unlike `alarm_clock` there is no day-of-week gate — this control is
// daily-only by design.

/** Minutes since local midnight, or null if `hhmm` isn't a valid HH:MM. */
function toMinutes(hhmm: string): number | null {
  if (typeof hhmm !== "string") return null;
  const parts = hhmm.split(":");
  if (parts.length !== 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isInteger(h) || !Number.isInteger(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
}

/**
 * Is `nowLocal` inside the [start, end) quiet-hours window?
 *
 * `end <= start` wraps past midnight — identical to how the `alarm_clock`
 * schedule reads its window (e.g. 22:00 → 07:00 covers 23:00 AND 03:00).
 * A malformed time yields `false` (fail OPEN — speak — never fail into
 * silence, since silence is the surprising failure mode for a voice the
 * household is expecting).
 *
 * NOTE: the caller is responsible for checking `tts_quiet_hours_enabled`.
 * This helper answers only "is now inside the window".
 */
export function isWithinQuietHours(
  nowLocal: Date,
  start: string,
  end: string,
): boolean {
  const s = toMinutes(start);
  const e = toMinutes(end);
  if (s == null || e == null) return false;

  // A zero-length window (start === end) silences nothing.
  if (s === e) return false;

  const minutes = nowLocal.getHours() * 60 + nowLocal.getMinutes();

  if (e > s) {
    // Same-day window, e.g. 13:00 → 15:00.
    return minutes >= s && minutes < e;
  }
  // Overnight wrap, e.g. 22:00 → 07:00: tonight's leg OR this morning's.
  return minutes >= s || minutes < e;
}
